import { LotQuery } from 'components/pages/LotBasePage/types/lotQuery'
import React from 'react'
import { LocationMarkerIcon, PhoneIcon } from '@heroicons/react/solid'
import formatPhone from 'utils/formatPhone'
import cn from 'classnames'
import { useRouter } from 'next/router'

import Map, { MarkerType } from './Map'

const MAP_HEIGHT = 600
type Props = {
  description: NonNullable<LotQuery['lot']>['description']
  marker: MarkerType
  lotName: string
  address: string
  organizationName: string
  phone: string | null
  email: string | null
}

const LotDescription = ({
  description,
  marker,
  lotName,
  address,
  organizationName,
  email,
  phone
}: Props) => {
  const router = useRouter()
  const hideMarketing =
    router.asPath.includes('boeing') || router.asPath.includes('homedepot')
  return (
    <div className="bg-gray-50 md:pt-20 pt-16 pb-16">
      <div
        className="scroll-offset mx-auto max-w-screen-md lg:max-w-7xl md:px-8 px-4"
        id="info"
      >
        <div>
          <h2 className="md:text-4xl text-3xl tracking-wide mb-4 md:mb-8 font-bold text-center">
            About {lotName}
          </h2>
        </div>
        <div className="md:mt-12 mt-2 flex gap-8 md:flex-row flex-col">
          <div
            className={cn('flex flex-grow md:w-1/2 w-full', {
              'flex-col-reverse md:flex-col': description.present,
              'flex-col-reverse': !description.present
            })}
          >
            {!hideMarketing && (
              <div>
                <Map
                  height={MAP_HEIGHT}
                  marker={marker}
                  markerAddress={address}
                  markerTitle={lotName}
                  scale={2}
                />
              </div>
            )}
            <div className="my-2">
              <div className="space-y-1 text-lg font-medium leading-6">
                <h3>Organizer</h3>
                <p className="text-blue-600">{organizationName}</p>
              </div>
              <div className="flex sm:space-x-5 flex-col sm:flex-row">
                {phone && (
                  <a
                    className="flex items-center hover:underline whitespace-nowrap"
                    href={`tel:${phone}`}
                  >
                    <PhoneIcon className="h-5 w-5 mr-1 text-gray-500" />
                    {formatPhone(phone)}
                  </a>
                )}
                <a
                  className="flex items-center hover:underline"
                  href={`mailto:${email}`}
                >
                  <div className="h-5 w-5 mr-1 text-gray-500">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                      <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                    </svg>
                  </div>
                  {email}
                </a>
              </div>
            </div>
          </div>

          {description.present && (
            <div>
              <div className="flex items-center bg-orange-50 border border-solid border-orange-300 text-sm text-orange-600 font-medium rounded-full w-max px-4 py-1 truncate max-w-full">
                <LocationMarkerIcon className="h-6 w-6" />
                <p className="ml-2 truncate">{address}</p>
              </div>
              <div
                className="prose text-justify mt-8"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: description.html }}
              />{' '}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LotDescription
